import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Pencil, LogOut, Upload, X, Save, Loader2, Calendar, Building2, BarChart3, Ticket, Euro, Mail, RefreshCcw, CheckCircle2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BRAND = '#F82C47';

const emptyEvent = { name: '', venue: '', location: '', day: '', description: '', image: '', inactive: false, venue_id: null, tickets: [] };
const emptyVenue = { name: '', location: '', image: '', priceFrom: 0, description: '', inactive: false };

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem('admin_token') || '');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editingVenue, setEditingVenue] = useState(null);
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState(null);
  const [resendingId, setResendingId] = useState('');

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const loadAll = useCallback(async () => {
    try {
      const [e, v] = await Promise.all([
        fetch(`${API}/admin/events`, { headers: authHeaders() }),
        fetch(`${API}/admin/venues`, { headers: authHeaders() }),
      ]);
      if (e.status === 401) { handleLogout(); return; }
      if (e.ok) setEvents(await e.json());
      if (v.ok) setVenues(await v.json());
    } catch (err) { console.error(err); }
  }, [authHeaders]);

  useEffect(() => { if (token) loadAll(); }, [token, loadAll]);

  const loadStats = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/stats`, { headers: authHeaders() });
      if (res.ok) setStats(await res.json());
    } catch (err) { console.error(err); }
  }, [authHeaders]);

  useEffect(() => { if (token && tab === 'stats') loadStats(); }, [token, tab, loadStats]);

  const loadOrders = useCallback(async () => {
    try {
      const res = await fetch(`${API}/admin/orders`, { headers: authHeaders() });
      if (res.ok) setOrders(await res.json());
    } catch (err) { console.error(err); }
  }, [authHeaders]);

  useEffect(() => { if (token && tab === 'orders') loadOrders(); }, [token, tab, loadOrders]);

  const resendEmail = async (pi, currentEmail) => {
    const to = window.prompt(`Reenviar email a (deja en blanco para usar el original ${currentEmail || '—'}):`, currentEmail || '');
    if (to === null) return; // cancelled
    setResendingId(pi);
    try {
      const res = await fetch(`${API}/admin/resend-email/${encodeURIComponent(pi)}`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify(to ? { email: to } : {}),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.sent) {
        alert(`✅ Email reenviado a ${data.to}`);
      } else {
        alert(`❌ Error: ${data.detail || 'no se pudo reenviar'}`);
      }
      loadOrders();
    } catch (err) {
      alert('Error de conexión');
    } finally {
      setResendingId('');
    }
  };

  const handleLogin = async (ev) => {
    ev.preventDefault();
    setLoading(true); setLoginError('');
    try {
      const res = await fetch(`${API}/admin/login`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, password }) });
      const data = await res.json();
      if (res.ok && data.token) {
        localStorage.setItem('admin_token', data.token);
        setToken(data.token);
      } else setLoginError(data.detail || 'Credenciales incorrectas');
    } catch (err) { setLoginError('Error de conexión'); }
    setLoading(false);
  };

  const handleLogout = () => { localStorage.removeItem('admin_token'); setToken(''); };

  const saveEvent = async (data) => {
    // Backend returns `id` (mapped from event_id) via /api/admin/events; support both keys here.
    const eid = data.event_id ?? data.id;
    const isNew = eid === undefined || eid === null;
    const url = isNew ? `${API}/admin/events` : `${API}/admin/events/${eid}`;
    const payload = { ...data };
    delete payload.event_id; delete payload.id; delete payload.slug;
    const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(payload) });
    if (res.ok) { setEditingEvent(null); loadAll(); } else { alert('Error al guardar'); }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm('¿Eliminar este evento?')) return;
    await fetch(`${API}/admin/events/${id}`, { method: 'DELETE', headers: authHeaders() });
    loadAll();
  };

  const saveVenue = async (data) => {
    const isNew = !data.venue_id && !data.id;
    const vid = data.venue_id || data.id;
    const url = isNew ? `${API}/admin/venues` : `${API}/admin/venues/${vid}`;
    const payload = {
      name: data.name,
      location: data.location,
      image: data.image,
      priceFrom: parseFloat(data.priceFrom) || 0,
      description: data.description,
      inactive: data.inactive,
    };
    const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: authHeaders(), body: JSON.stringify(payload) });
    if (res.ok) { setEditingVenue(null); loadAll(); } else { alert('Error al guardar'); }
  };

  const deleteVenue = async (id) => {
    if (!window.confirm('¿Eliminar este venue?')) return;
    await fetch(`${API}/admin/venues/${id}`, { method: 'DELETE', headers: authHeaders() });
    loadAll();
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center p-5">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-[#1a1a1c] rounded-2xl p-8" data-testid="admin-login-form">
          <h1 className="text-white text-2xl font-bold mb-1">Clubbers Admin</h1>
          <p className="text-gray-400 text-sm mb-6">Panel de administración</p>
          <input data-testid="admin-email-input" type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full bg-[#2a2a2a] border border-gray-700 rounded-xl px-4 py-3 text-white mb-3 outline-none focus:border-[#F82C47]" />
          <input data-testid="admin-password-input" type="password" placeholder="Contraseña" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full bg-[#2a2a2a] border border-gray-700 rounded-xl px-4 py-3 text-white mb-4 outline-none focus:border-[#F82C47]" />
          {loginError && <p className="text-[#F82C47] text-sm mb-3" data-testid="admin-login-error">{loginError}</p>}
          <button data-testid="admin-login-button" type="submit" disabled={loading} className="w-full py-3 rounded-full text-white font-bold flex items-center justify-center gap-2" style={{ backgroundColor: BRAND }}>
            {loading ? <Loader2 size={18} className="animate-spin" /> : 'Entrar'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0b] pb-20">
      <header className="sticky top-0 z-10 bg-[#0a0a0b]/90 backdrop-blur border-b border-gray-800 px-5 py-4 flex items-center justify-between">
        <h1 className="text-white text-xl font-bold">Clubbers Admin</h1>
        <button onClick={handleLogout} className="text-gray-400 hover:text-white flex items-center gap-1.5 text-sm" data-testid="admin-logout-button"><LogOut size={16} /> Salir</button>
      </header>

      <div className="flex gap-2 px-5 py-4">
        <button onClick={() => setTab('events')} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${tab === 'events' ? 'text-white' : 'text-gray-400 bg-[#1a1a1c]'}`} style={tab === 'events' ? { backgroundColor: BRAND } : {}} data-testid="admin-tab-events"><Calendar size={15} /> Eventos</button>
        <button onClick={() => setTab('venues')} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${tab === 'venues' ? 'text-white' : 'text-gray-400 bg-[#1a1a1c]'}`} style={tab === 'venues' ? { backgroundColor: BRAND } : {}} data-testid="admin-tab-venues"><Building2 size={15} /> Venues</button>
        <button onClick={() => setTab('stats')} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${tab === 'stats' ? 'text-white' : 'text-gray-400 bg-[#1a1a1c]'}`} style={tab === 'stats' ? { backgroundColor: BRAND } : {}} data-testid="admin-tab-stats"><BarChart3 size={15} /> Ventas</button>
        <button onClick={() => setTab('orders')} className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold ${tab === 'orders' ? 'text-white' : 'text-gray-400 bg-[#1a1a1c]'}`} style={tab === 'orders' ? { backgroundColor: BRAND } : {}} data-testid="admin-tab-orders"><Mail size={15} /> Pedidos</button>
      </div>

      {tab === 'events' && (
        <div className="px-5">
          <button onClick={() => setEditingEvent({ ...emptyEvent })} className="mb-4 flex items-center gap-2 px-4 py-2.5 rounded-full text-white font-semibold text-sm" style={{ backgroundColor: BRAND }} data-testid="admin-new-event-button"><Plus size={16} /> Nuevo evento</button>
          <div className="space-y-3">
            {events.map((e) => (
              <div key={e.id} className="bg-[#1a1a1c] rounded-xl p-4 flex items-center gap-3" data-testid={`admin-event-row-${e.id}`}>
                {e.image ? <img src={e.image} alt="" className="w-14 h-14 rounded-lg object-cover bg-[#2a2a2a]" /> : <div className="w-14 h-14 rounded-lg bg-[#2a2a2a]" />}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{e.name}</p>
                  <p className="text-gray-400 text-xs truncate">{e.day} · {e.location} {e.inactive ? '· (inactivo)' : ''}</p>
                </div>
                <button onClick={() => setEditingEvent(e)} className="text-gray-300 hover:text-white p-2" data-testid={`admin-edit-event-${e.id}`}><Pencil size={18} /></button>
                <button onClick={() => deleteEvent(e.id)} className="text-gray-300 hover:text-[#F82C47] p-2" data-testid={`admin-delete-event-${e.id}`}><Trash2 size={18} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'venues' && (
        <div className="px-5">
          <button onClick={() => setEditingVenue({ ...emptyVenue })} className="mb-4 flex items-center gap-2 px-4 py-2.5 rounded-full text-white font-semibold text-sm" style={{ backgroundColor: BRAND }} data-testid="admin-new-venue-button"><Plus size={16} /> Nuevo venue</button>
          <div className="space-y-3">
            {venues.map((v) => (
              <div key={v.id} className="bg-[#1a1a1c] rounded-xl p-4 flex items-center gap-3" data-testid={`admin-venue-row-${v.id}`}>
                {v.image ? <img src={v.image} alt="" className="w-14 h-14 rounded-lg object-cover bg-[#2a2a2a]" /> : <div className="w-14 h-14 rounded-lg bg-[#2a2a2a]" />}
                <div className="flex-1 min-w-0">
                  <p className="text-white font-semibold truncate">{v.name}</p>
                  <p className="text-gray-400 text-xs truncate">{v.location} {v.inactive ? '· (inactivo)' : ''}</p>
                </div>
                <button onClick={() => setEditingVenue(v)} className="text-gray-300 hover:text-white p-2"><Pencil size={18} /></button>
                <button onClick={() => deleteVenue(v.id)} className="text-gray-300 hover:text-[#F82C47] p-2"><Trash2 size={18} /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'stats' && (
        <div className="px-5" data-testid="admin-stats">
          {!stats ? (
            <div className="flex items-center justify-center py-10 text-gray-400 gap-2"><Loader2 size={18} className="animate-spin" /> Cargando...</div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-[#1a1a1c] rounded-xl p-4">
                  <div className="flex items-center gap-1.5 text-gray-400 text-xs mb-1"><Euro size={13} /> Ingresos</div>
                  <p className="text-white text-lg font-bold" data-testid="stats-total-revenue">{stats.totals.revenue.toFixed(2)}€</p>
                </div>
                <div className="bg-[#1a1a1c] rounded-xl p-4">
                  <div className="flex items-center gap-1.5 text-gray-400 text-xs mb-1"><Ticket size={13} /> Entradas</div>
                  <p className="text-white text-lg font-bold" data-testid="stats-total-tickets">{stats.totals.tickets}</p>
                </div>
                <div className="bg-[#1a1a1c] rounded-xl p-4">
                  <div className="flex items-center gap-1.5 text-gray-400 text-xs mb-1"><BarChart3 size={13} /> Pedidos</div>
                  <p className="text-white text-lg font-bold">{stats.totals.orders}</p>
                </div>
              </div>
              <h3 className="text-white font-semibold text-sm mb-3">Ventas por evento</h3>
              {stats.events.length === 0 ? (
                <p className="text-gray-400 text-sm">Aún no hay ventas registradas.</p>
              ) : (
                <div className="space-y-3">
                  {stats.events.map((e) => (
                    <div key={e.event_id} className="bg-[#1a1a1c] rounded-xl p-4 flex items-center gap-3" data-testid={`stats-event-${e.event_id}`}>
                      {e.image ? <img src={e.image} alt="" className="w-12 h-12 rounded-lg object-cover bg-[#2a2a2a]" /> : <div className="w-12 h-12 rounded-lg bg-[#2a2a2a]" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold truncate">{e.name}</p>
                        <p className="text-gray-400 text-xs">{e.tickets} entradas · {e.orders} pedidos</p>
                      </div>
                      <p className="text-[#F82C47] font-bold">{e.revenue.toFixed(2)}€</p>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tab === 'orders' && (
        <div className="px-5" data-testid="admin-orders">
          <div className="flex items-center justify-between mb-3">
            <p className="text-gray-400 text-xs">Últimos 100 pedidos · Reenvía el email si el cliente puso mal el correo.</p>
            <button onClick={loadOrders} className="text-white text-xs flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#1a1a1c]" data-testid="orders-refresh"><RefreshCcw size={12} /> Refrescar</button>
          </div>
          {!orders ? (
            <div className="flex items-center justify-center py-10 text-gray-400 gap-2"><Loader2 size={18} className="animate-spin" /> Cargando...</div>
          ) : orders.length === 0 ? (
            <p className="text-gray-400 text-sm">Aún no hay pedidos.</p>
          ) : (
            <div className="space-y-2">
              {orders.map((o) => (
                <div key={o.payment_intent_id} className="bg-[#1a1a1c] rounded-xl p-3" data-testid={`order-row-${o.payment_intent_id}`}>
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-semibold truncate">{o.event_name}</p>
                      <p className="text-gray-400 text-xs truncate">{o.buyer_name || '—'} · <span className={o.buyer_email ? 'text-white' : 'text-red-400'}>{o.buyer_email || 'sin email'}</span></p>
                    </div>
                    <div className="text-right shrink-0 ml-3">
                      <p className="text-[#F82C47] font-bold text-sm">{Number(o.amount || 0).toFixed(2)}€</p>
                      <p className="text-gray-500 text-[10px]">x{o.quantity}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                    <div className="flex items-center gap-1.5 text-[11px]">
                      {o.payment_status === 'paid' ? (
                        <span className="text-green-400 flex items-center gap-1"><CheckCircle2 size={11} /> Pagado</span>
                      ) : (
                        <span className="text-gray-500">{o.payment_status || 'pendiente'}</span>
                      )}
                      <span className="text-gray-600">·</span>
                      {o.email_sent ? (
                        <span className="text-green-400 flex items-center gap-1"><Mail size={11} /> Enviado</span>
                      ) : (
                        <span className="text-yellow-400 flex items-center gap-1"><AlertCircle size={11} /> Sin enviar</span>
                      )}
                    </div>
                    <button
                      onClick={() => resendEmail(o.payment_intent_id, o.buyer_email)}
                      disabled={resendingId === o.payment_intent_id || o.payment_status !== 'paid'}
                      className="text-white text-[11px] font-semibold px-3 py-1.5 rounded-full disabled:opacity-40 flex items-center gap-1"
                      style={{ backgroundColor: BRAND }}
                      data-testid={`resend-email-${o.payment_intent_id}`}
                    >
                      {resendingId === o.payment_intent_id
                        ? <><Loader2 size={11} className="animate-spin" /> Enviando…</>
                        : <><Mail size={11} /> Reenviar email</>}
                    </button>
                  </div>
                  <p className="text-gray-600 text-[10px] font-mono mt-1.5 truncate">{o.payment_intent_id}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}


      {editingEvent && <EventForm initial={editingEvent} token={token} onClose={() => setEditingEvent(null)} onSave={saveEvent} />}
      {editingVenue && <VenueForm initial={editingVenue} events={events} token={token} onClose={() => setEditingVenue(null)} onSave={saveVenue} />}
    </div>
  );
}

const inputClass = "w-full bg-[#2a2a2a] border border-gray-700 rounded-xl px-4 py-3 text-white outline-none focus:border-[#F82C47]";

function ImageUploader({ token, value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/admin/upload`, { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (res.ok && data.url) onChange(data.url);
      else alert('Error al subir imagen');
    } catch (err) { alert('Error al subir imagen'); }
    setUploading(false);
  };
  return (
    <div>
      <label className="text-gray-400 text-xs">Flyer / Imagen</label>
      <div className="flex items-center gap-3 mt-1">
        {value ? <img src={value} alt="" className="w-20 h-20 rounded-lg object-cover bg-[#2a2a2a]" /> : <div className="w-20 h-20 rounded-lg bg-[#2a2a2a] flex items-center justify-center text-gray-600"><Upload size={20} /></div>}
        <label className="cursor-pointer px-4 py-2 rounded-full bg-[#2a2a2a] text-white text-sm font-semibold flex items-center gap-2" data-testid="admin-upload-label">
          {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {uploading ? 'Subiendo...' : 'Subir imagen'}
          <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="admin-image-input" />
        </label>
      </div>
      <input className={`${inputClass} mt-2 text-xs`} placeholder="o pega una URL de imagen" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function EventForm({ initial, token, onClose, onSave }) {
  const [f, setF] = useState({ ...initial, tickets: initial.tickets || [] });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setTicket = (i, k, v) => setF((p) => { const t = [...p.tickets]; t[i] = { ...t[i], [k]: v }; return { ...p, tickets: t }; });
  const addTicket = () => setF((p) => ({ ...p, tickets: [...p.tickets, { id: (p.tickets.reduce((m, t) => Math.max(m, t.id || 0), 0) + 1), price: 0, label: '', note: '', soldOut: false, details: [] }] }));
  const removeTicket = (i) => setF((p) => ({ ...p, tickets: p.tickets.filter((_, idx) => idx !== i) }));

  const submit = () => {
    if (!f.name.trim()) { alert('El nombre es obligatorio'); return; }
    const tickets = f.tickets.map((t) => ({
      id: parseInt(t.id, 10), price: parseFloat(t.price) || 0, label: t.label || `${t.price}€`,
      note: t.note || null, soldOut: !!t.soldOut,
      details: Array.isArray(t.details) ? t.details : (t.details ? String(t.details).split('\n').filter(Boolean) : []),
    }));
    onSave({
      ...f,
      price: parseFloat(f.price) || (tickets.find((t) => !t.soldOut)?.price ?? 0),
      tickets,
      // FASE 1 — nuevos campos
      estilos: typeof f.estilos === 'string' ? f.estilos.split(',').map((s) => s.trim()).filter(Boolean) : (f.estilos || []),
      tipo_plan: f.tipo_plan || 'discoteca',
      edad_minima: parseInt(f.edad_minima, 10) || 18,
      ultimas_entradas: !!f.ultimas_entradas,
      provider: f.provider || 'stripe',
      external_event_id: f.external_event_id || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-[#1a1a1c] w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="admin-event-form">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white text-lg font-bold">{(f.event_id ?? f.id) ? 'Editar evento' : 'Nuevo evento'}</h2>
          <button onClick={onClose} className="text-gray-400"><X size={22} /></button>
        </div>
        <div className="space-y-3">
          <div><label className="text-gray-400 text-xs">Nombre del evento</label><input className={inputClass} value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="event-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-gray-400 text-xs">Venue / Club</label><input className={inputClass} value={f.venue} onChange={(e) => set('venue', e.target.value)} data-testid="event-venue-input" /></div>
            <div><label className="text-gray-400 text-xs">Ciudad</label><input className={inputClass} value={f.location} onChange={(e) => set('location', e.target.value)} data-testid="event-location-input" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-gray-400 text-xs">Fecha (ej. "12 de Septiembre")</label><input className={inputClass} value={f.day} onChange={(e) => set('day', e.target.value)} data-testid="event-day-input" /></div>
            <div className="flex items-end"><label className="flex items-center gap-2 text-white text-sm py-3"><input type="checkbox" checked={f.inactive} onChange={(e) => set('inactive', e.target.checked)} data-testid="event-inactive-checkbox" /> Inactivo</label></div>
          </div>
          <div><label className="text-gray-400 text-xs">Descripción</label><input className={inputClass} value={f.description} onChange={(e) => set('description', e.target.value)} data-testid="event-description-input" /></div>
          <ImageUploader token={token} value={f.image} onChange={(v) => set('image', v)} />

          <div className="pt-2 border-t border-white/5">
            <p className="text-white font-semibold text-sm mb-2">Clasificación y provider</p>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-gray-400 text-xs">Tipo de plan</label>
                <select className={inputClass} value={f.tipo_plan || 'discoteca'} onChange={(e) => set('tipo_plan', e.target.value)} data-testid="event-tipoplan-input">
                  <option value="discoteca">Discoteca</option>
                  <option value="universitaria">Fiesta universitaria</option>
                  <option value="terraza">Terraza</option>
                  <option value="viaje">Viaje</option>
                </select>
              </div>
              <div><label className="text-gray-400 text-xs">Edad mínima</label>
                <input type="number" className={inputClass} value={f.edad_minima ?? 18} onChange={(e) => set('edad_minima', e.target.value)} data-testid="event-edadminima-input" />
              </div>
            </div>
            <div className="mt-3"><label className="text-gray-400 text-xs">Estilos (separados por coma, ej. "Techno, House")</label>
              <input className={inputClass} value={Array.isArray(f.estilos) ? f.estilos.join(', ') : (f.estilos || '')} onChange={(e) => set('estilos', e.target.value)} data-testid="event-estilos-input" />
            </div>
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><label className="text-gray-400 text-xs">Provider de compra</label>
                <select className={inputClass} value={f.provider || 'stripe'} onChange={(e) => set('provider', e.target.value)} data-testid="event-provider-input">
                  <option value="stripe">Stripe (propio)</option>
                  <option value="fourvenues">Fourvenues</option>
                  <option value="whan">Whan</option>
                </select>
              </div>
              <div className="flex items-end"><label className="flex items-center gap-2 text-white text-sm py-3"><input type="checkbox" checked={!!f.ultimas_entradas} onChange={(e) => set('ultimas_entradas', e.target.checked)} data-testid="event-ultimasentradas-checkbox" /> Últimas entradas</label></div>
            </div>
            {(f.provider && f.provider !== 'stripe') && (
              <div className="mt-3"><label className="text-gray-400 text-xs">ID externo del evento ({f.provider})</label>
                <input className={inputClass} value={f.external_event_id || ''} onChange={(e) => set('external_event_id', e.target.value)} placeholder="ID en la plataforma externa" data-testid="event-external-id-input" />
              </div>
            )}
          </div>

          <div className="pt-2">
            <div className="flex items-center justify-between mb-2">
              <label className="text-white font-semibold text-sm">Entradas</label>
              <button onClick={addTicket} className="text-[#F82C47] text-sm flex items-center gap-1" data-testid="add-ticket-button"><Plus size={14} /> Añadir</button>
            </div>
            <div className="space-y-3">
              {f.tickets.map((t, i) => (
                <div key={i} className="bg-[#2a2a2a] rounded-xl p-3 space-y-2" data-testid={`ticket-row-${i}`}>
                  <div className="grid grid-cols-3 gap-2">
                    <input className="bg-[#1a1a1c] border border-gray-700 rounded-lg px-2 py-2 text-white text-sm" placeholder="Etiqueta" value={t.label} onChange={(e) => setTicket(i, 'label', e.target.value)} />
                    <input className="bg-[#1a1a1c] border border-gray-700 rounded-lg px-2 py-2 text-white text-sm" placeholder="Precio €" type="number" value={t.price} onChange={(e) => setTicket(i, 'price', e.target.value)} />
                    <input className="bg-[#1a1a1c] border border-gray-700 rounded-lg px-2 py-2 text-white text-sm" placeholder="Aviso" value={t.note || ''} onChange={(e) => setTicket(i, 'note', e.target.value)} />
                  </div>
                  <textarea className="w-full bg-[#1a1a1c] border border-gray-700 rounded-lg px-2 py-2 text-white text-xs" placeholder="Detalles (uno por línea, opcional)" rows={2} value={Array.isArray(t.details) ? t.details.join('\n') : (t.details || '')} onChange={(e) => setTicket(i, 'details', e.target.value.split('\n'))} />
                  <div className="flex items-center justify-between">
                    <label className="flex items-center gap-2 text-white text-xs"><input type="checkbox" checked={!!t.soldOut} onChange={(e) => setTicket(i, 'soldOut', e.target.checked)} /> Agotada</label>
                    <button onClick={() => removeTicket(i)} className="text-gray-400 hover:text-[#F82C47] text-xs flex items-center gap-1"><Trash2 size={13} /> Quitar</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button onClick={submit} className="w-full py-3 rounded-full text-white font-bold flex items-center justify-center gap-2 mt-2" style={{ backgroundColor: BRAND }} data-testid="save-event-button"><Save size={18} /> Guardar</button>
        </div>
      </div>
    </div>
  );
}

function VenueForm({ initial, events = [], token, onClose, onSave }) {
  const venueId = initial.venue_id || initial.id || null;
  const [f, setF] = useState({ ...initial });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Auto-link preview: any event whose `venue` name matches this venue's name (case-insensitive)
  const normalizedName = (f.name || '').trim().toLowerCase();
  const matchingEvents = normalizedName
    ? events.filter((e) => (e.venue || '').trim().toLowerCase() === normalizedName)
    : [];

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-[#1a1a1c] w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl p-5" onClick={(e) => e.stopPropagation()} data-testid="admin-venue-form">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white text-lg font-bold">{venueId ? 'Editar venue' : 'Nuevo venue'}</h2>
          <button onClick={onClose} className="text-gray-400"><X size={22} /></button>
        </div>
        <div className="space-y-3">
          <div><label className="text-gray-400 text-xs">Nombre</label><input className={inputClass} value={f.name} onChange={(e) => set('name', e.target.value)} data-testid="venue-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-gray-400 text-xs">Ubicación</label><input className={inputClass} value={f.location} onChange={(e) => set('location', e.target.value)} data-testid="venue-location-input" /></div>
            <div><label className="text-gray-400 text-xs">Precio desde €</label><input className={inputClass} type="number" value={f.priceFrom} onChange={(e) => set('priceFrom', e.target.value)} /></div>
          </div>
          <div><label className="text-gray-400 text-xs">Descripción</label><input className={inputClass} value={f.description} onChange={(e) => set('description', e.target.value)} /></div>
          <ImageUploader token={token} value={f.image} onChange={(v) => set('image', v)} />
          <label className="flex items-center gap-2 text-white text-sm"><input type="checkbox" checked={f.inactive} onChange={(e) => set('inactive', e.target.checked)} /> Inactivo</label>

          <div className="pt-2" data-testid="venue-events-auto">
            <label className="text-white font-semibold text-sm">Eventos vinculados</label>
            <p className="text-gray-500 text-xs mb-2">
              Se vinculan automáticamente los eventos cuyo <span className="text-white">Venue / Club</span> coincida con el nombre de esta sala.
            </p>
            {!normalizedName ? (
              <p className="text-gray-400 text-xs">Introduce un nombre para ver los eventos que se vincularán.</p>
            ) : matchingEvents.length === 0 ? (
              <p className="text-gray-400 text-xs">Aún no hay eventos cuyo venue se llame "{f.name}".</p>
            ) : (
              <div className="max-h-44 overflow-y-auto bg-[#2a2a2a] rounded-xl p-2 space-y-1">
                {matchingEvents.map((e) => (
                  <div key={e.id} className="flex items-center gap-2 px-2 py-1.5 text-sm" data-testid={`venue-event-auto-${e.id}`}>
                    <span className="text-[#F82C47]">●</span>
                    <span className="text-white flex-1 truncate">{e.name}</span>
                    <span className="text-gray-500 text-xs">{e.day}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button onClick={() => onSave(f)} className="w-full py-3 rounded-full text-white font-bold flex items-center justify-center gap-2" style={{ backgroundColor: BRAND }} data-testid="save-venue-button"><Save size={18} /> Guardar</button>
        </div>
      </div>
    </div>
  );
}
